import React, { useEffect } from "react";

import { App } from "@common/models";
import { getRouteFromPath } from "@common/util/moduleRouteHelper";
import { setShakeSdkAppMetadata } from "@common/util/shakeSdkUtils";
import { useAppContext } from "@main/context/App/App.context";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { SubscriptionState, useSubscriptionContext } from "@main/context/Subscription/SubscriptionContext";
import { useWorkspaceAppParams } from "@main/pages/IntegrationActivation/helpers/useWorkspaceAppParams";
import { generatePath, Outlet, useLocation, useNavigate } from "react-router-dom";

import { PageNavigation } from "../components/PageNavigation/PageNavigation";

export function ModuleLayoutView() {
  const { apps, loading: appsLoading } = useAppContext();
  const { selectedWorkspace, selectedApp } = useAppSelectionContext();

  const navigate = useNavigate();
  const location = useLocation();
  const params = useWorkspaceAppParams();
  const { state } = useSubscriptionContext();

  useEffect(() => {
    if (appsLoading || selectedApp || !selectedWorkspace || state !== SubscriptionState.good) return;
    // `apps` carries no workspace of its own, so right after a workspace change it can still hold
    // the previous workspace's list. Picking from it would put a foreign app key in the URL.
    const workspaceApps = apps.filter((app) => app.team_id === selectedWorkspace.id);
    if (!workspaceApps.length) return;
    const pickedApp = getSelectedApp(workspaceApps);

    const routePath = getRouteFromPath(location.pathname);
    pickedApp &&
      navigate(generatePath(routePath, { workspaceSlug: selectedWorkspace.slug, appKey: pickedApp.key }), {
        replace: true,
      });
  }, [appsLoading, selectedApp, selectedWorkspace, state, apps, params, location.pathname, navigate]);

  return (
    <PageNavigation>
      <Outlet />
    </PageNavigation>
  );
}

function getSelectedApp(availableApps: App[]) {
  const activeApps = availableApps.filter((a) => a.active);
  const localAppKey = localStorage.getItem("localAppKey");

  // Check if localAppKey corresponds to an active app for override
  const localApp = localAppKey ? activeApps.find((a) => a.key === localAppKey) : undefined;
  if (localApp) {
    setShakeSdkAppMetadata(localApp);
    return localApp;
  }

  // Filter non-sample active apps
  const nonSampleApps = activeApps.filter((a) => !a.is_sample);

  // Select latest created non-sample app if any, else first active app or undefined
  const selectedApp =
    nonSampleApps.length > 0
      ? nonSampleApps.reduce((latest, current) => (current.created > latest.created ? current : latest))
      : activeApps[0];

  setShakeSdkAppMetadata(selectedApp);
  return selectedApp;
}
